/**
 * Guided recovery for a box (#804): when Reset to maintenance and Reinstall
 * are offered, what to warn about, and what a refusal means.
 *
 * Recovery used to be gated on status: "Reset to maintenance" only rendered
 * for `error` / `config_apply_failed`, so a `ready` box with "Node unreachable
 * after config apply" had no action anywhere, and Reinstall waited on a
 * `maintenanceDetected` flag a 5 s probe often never set (#788). Both are now
 * offered for every box on a site that isn't decommissioned; the
 * deployment-service is the authority on whether the hop is legal right now,
 * and its 409s are shown with what to do about them.
 */
import type { Machine } from '@/types';
import { parseApiError } from '@/lib/apiError';
import { siderolinkFresh, type Indicator } from '@/lib/boxes';
import { ROLE_LABELS } from '@/lib/roleDisplay';

export type RecoveryAction = 'reset' | 'reinstall';

/** talos-reset needs a handshake this recent (machines.ts SIDEROLINK_LIVE_WINDOW_MS). */
export const RESET_LIVE_WINDOW_MS = 60_000;

export interface RecoveryEligibility {
  reset: boolean;
  reinstall: boolean;
  /** Why neither is offered, when they aren't. */
  reason: string | null;
}

export function recoveryEligibility(m: Machine | null): RecoveryEligibility {
  if (!m) return { reset: false, reinstall: false, reason: 'This node has no Jeket machine record.' };
  if (!m.siteId || m.status === 'registered') {
    return { reset: false, reinstall: false, reason: 'The box is not assigned to a site.' };
  }
  if (m.status === 'decommissioned') {
    return { reset: false, reinstall: false, reason: 'The box is decommissioned.' };
  }
  return { reset: true, reinstall: true, reason: null };
}

/**
 * The action to put on the row itself (both stay in its menu regardless):
 * Reinstall for a box sitting in maintenance mode; Reset for a box that is
 * stopped or wedged but still reachable over its tunnel. A box whose tunnel is
 * down can't be reset remotely — that needs the install image, which the
 * dialog explains.
 */
export function suggestedRecovery(m: Machine | null, indicators: Indicator[]): RecoveryAction | null {
  const elig = recoveryEligibility(m);
  if (!m || !elig.reset) return null;
  if (m.maintenanceDetected) return 'reinstall';
  const tunnelUp = indicators.find((i) => i.key === 'siderolink')?.state === 'ok';
  if (!tunnelUp) return null;
  if (m.status === 'error' || m.status === 'config_apply_failed') return 'reset';
  const talos = indicators.find((i) => i.key === 'talos');
  if (talos?.state === 'fail' && (m.apidState === 'refused' || m.apidState === 'timeout')) return 'reset';
  return null;
}

// ---- Re-flash approval (JSS#883 security review) ----
//
// A box that has proven its own reconnect key rejoins from the site's USB
// image (after a whole-disk re-flash wiped that key) only inside a window an
// Admin opens. Otherwise the USB plus the box's hardware ID, which isn't
// secret, would be enough to take over its identity. deployment-service
// refuses the rejoin (409 `reflash_requires_approval`) and records when.
//
// Anyone holding the USB can cause such a refusal on a healthy box, so the
// window is only offered once the box has stopped connecting with its own
// key as well (#900): its tunnel is down, and it has not connected with its
// key since the refusal. Otherwise the row shows who that was — not this box
// — and no button; deployment-service refuses the window too
// (409 `box_still_authenticating`).

/** A refusal this recent puts "Allow re-flash" on the row itself. */
export const REFLASH_REFUSAL_RECENT_MS = 72 * 3_600_000;
/** The window the customer console opens (deployment-service's default; it allows 1–72 h). */
export const REFLASH_WINDOW_HOURS = 24;

export type ReflashApproval =
  | { kind: 'none' }
  /** The window is open: the box may rejoin from the USB image until `until`. */
  | { kind: 'open'; until: string }
  /**
   * Offer "Allow re-flash". `refusedAt` is set when a rejoin was refused
   * recently and no window has been opened since: that puts it on the row
   * itself. Otherwise it is offered (in the menu) because the box is offline.
   */
  | { kind: 'offer'; refusedAt: string | null }
  /**
   * A rejoin as this box was refused recently, but the box still connects
   * with its own key: it was not re-flashed, someone presented its identity
   * with the site's USB image. A warning, never the button (#900).
   */
  | { kind: 'impersonated'; refusedAt: string };

function time(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Whether the box has proven its own reconnect key (read back sealed, or
 * presented). `provenAt` survives a rotation of the key (JSS#895); older
 * deployment-services don't send it.
 */
export function reconnectProven(m: Machine | null): boolean {
  return !!(m?.reconnect?.provenAt || m?.reconnect?.verifiedAt || m?.reconnect?.sealedAt);
}

/**
 * Whether the box still connects to Jeket with its own key (#900): its
 * tunnel is up, or it connected with its key after the last refused rejoin.
 * The same rule deployment-service applies before it opens a window.
 */
export function stillConnectsAsItself(m: Machine, now: number): boolean {
  if (siderolinkFresh(m, now)) return true;
  const used = time(m.reconnect?.lastUsedAt);
  const refused = time(m.reflash?.refusedAt);
  return used !== null && refused !== null && used > refused;
}

export function reflashApproval(m: Machine | null, now: number): ReflashApproval {
  if (!m) return { kind: 'none' };
  const until = time(m.reflash?.allowedUntil);
  if (until !== null && until > now) return { kind: 'open', until: m.reflash!.allowedUntil! };
  // A box that hasn't proven its key rejoins from the USB without approval.
  if (!recoveryEligibility(m).reset || !reconnectProven(m)) return { kind: 'none' };
  const refused = time(m.reflash?.refusedAt);
  // Refusals only happen while the window is closed, so a window that ends
  // after the refusal was opened after it: that refusal has been answered.
  const answered = refused !== null && until !== null && until > refused;
  const recent = refused !== null && !answered && now - refused <= REFLASH_REFUSAL_RECENT_MS;
  if (stillConnectsAsItself(m, now)) {
    return recent ? { kind: 'impersonated', refusedAt: m.reflash!.refusedAt! } : { kind: 'none' };
  }
  return { kind: 'offer', refusedAt: recent ? m.reflash!.refusedAt! : null };
}

/** "Sep 26, 3:15 PM" in the viewer's locale and time zone. */
export function formatLocalDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ---- Control-plane impact ----

/** Statuses in which a control-plane box is (or is becoming) an etcd member. */
const CP_MEMBER_STATUSES: ReadonlySet<string> = new Set([
  'ready', 'cordoned', 'upgrading', 'installing_prereqs', 'bootstrapping', 'joining', 'error',
]);
/** Members counted as able to vote after the reset. */
const CP_HEALTHY_STATUSES: ReadonlySet<string> = new Set(['ready', 'cordoned', 'upgrading', 'installing_prereqs']);

export interface ControlPlaneImpact {
  isControlPlane: boolean;
  /** The target is an etcd member (not a control plane still being set up). */
  isMember: boolean;
  members: number;
  /** Healthy members left once the target is wiped. */
  healthyAfter: number;
  /** Members needed to keep etcd (and so Kubernetes) running. */
  quorum: number;
  onlyControlPlane: boolean;
  losesQuorum: boolean;
}

/**
 * What wiping `target` does to the site's control plane. talos-reset is not
 * graceful (the box doesn't leave etcd first), so its member stays counted
 * while the healthy count drops. On a one-control-plane site that is the
 * whole cluster: the only copy of its state is erased.
 */
export function controlPlaneImpact(target: Machine, siteMachines: Machine[]): ControlPlaneImpact {
  const cps = siteMachines.filter(
    (m) => m.siteId === target.siteId && m.role === 'controlplane' && CP_MEMBER_STATUSES.has(m.status),
  );
  const members = cps.length;
  const isControlPlane = target.role === 'controlplane';
  const isMember = isControlPlane && cps.some((m) => m.id === target.id);
  const healthy = cps.filter((m) => CP_HEALTHY_STATUSES.has(m.status));
  const healthyAfter = healthy.filter((m) => m.id !== target.id).length;
  const quorum = Math.floor(members / 2) + 1;
  return {
    isControlPlane,
    isMember,
    members,
    healthyAfter,
    quorum,
    onlyControlPlane: isMember && members === 1,
    losesQuorum: isMember && healthyAfter < quorum,
  };
}

// ---- Refusals ----

export interface RecoveryError {
  message: string;
  /** What to do about it, when the refusal is one we recognise. */
  hint: string | null;
}

/**
 * Turn a recovery route's failure into words. The 409s come from
 * routes/machines.ts: illegal state-machine hop, a concurrent status change,
 * not in maintenance mode, SideroLink not live / no address, and (template
 * assignment) hardware not yet discovered.
 */
/** The BFF's gate per action (orchestrator backend index.ts), for a 403 that doesn't name it. */
const DEFAULT_REQUIRED_ROLE: Record<RecoveryAction | 'remove' | 'update' | 'allow-reflash', string> = {
  reset: 'Admin',
  reinstall: 'Engineer',
  remove: 'Admin',
  update: 'Site Operator',
  'allow-reflash': 'Admin',
};

export function recoveryErrorMessage(
  err: unknown,
  action: RecoveryAction | 'remove' | 'update' | 'allow-reflash',
): RecoveryError {
  const e = parseApiError(err);
  if (e.status === 403) {
    const required = (e.body as { requiredRole?: string } | null)?.requiredRole;
    const role = required ? ROLE_LABELS[required] ?? required : DEFAULT_REQUIRED_ROLE[action];
    return { message: `You need the ${role} role to do this.`, hint: null };
  }
  const msg = e.message;
  const hints: Array<[RegExp, string]> = [
    [/invalid machine transition/i,
      "The box's current state doesn't allow this. The console may be showing an older state: wait a few seconds for it to refresh, then try again."],
    [/changed concurrently/i,
      'The box changed state while you were confirming. Check its new status before trying again.'],
    [/not in maintenance mode/i,
      'Reinstall only works on a box in maintenance mode. Reset it to maintenance first, or start it from the site install image.'],
    [/not live|last seen/i,
      "The box must be online for a reset. Check its power and network. If it can't be reached, start it from the site install image instead."],
    [/no siderolink address/i,
      'The box has never connected over its management tunnel. Start it from the site install image.'],
    [/hardware not yet discovered/i,
      'Wait for hardware discovery to finish, or reinstall the box to run discovery again.'],
    [/was not re-flashed/i,
      "Don't allow a rejoin for this box. Replace the site's USB image: whoever has a copy can present this box's identity."],
  ];
  const hint = hints.find(([re]) => re.test(msg))?.[1] ?? null;
  return { message: msg, hint };
}
